/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'
import { Navigate } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiButton, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiRouterLinkButtonEmpty } from '@modules/cui/RouterButton'
import {
  getLoginUrl,
  hasAnySsoMethod,
  hasRedirectOnMount,
  redirectOnMount,
} from '@modules/auth/auth'

import { getLoginFormSsoStyle } from '@/components/Login/loginStyles'

import AppLoadingRoot from '../../../../../components/AppLoadingRoot'
import LandingPage from '../../../../../components/LandingPage'

import type { AllProps } from './types'

class SsoBasedLogin extends Component<AllProps> {
  componentDidMount() {
    const {
      authMethods,
      fetchAuthMethods,
      fetchAuthMethodsRequest,
      redirectAfterLogin,
      redirectTo,
      newBearerToken,
    } = this.props

    if (hasRedirectOnMount(newBearerToken)) {
      redirectOnMount({ redirectAfterLogin, redirectTo, newBearerToken })
      return
    }

    if (!authMethods && !fetchAuthMethodsRequest.inProgress) {
      fetchAuthMethods()
    }
  }

  render() {
    const {
      loginRequest,
      authMethods,
      fetchAuthMethodsRequest,
      router: { location },
    } = this.props
    const canSso = hasAnySsoMethod(authMethods)

    if (authMethods === null || fetchAuthMethodsRequest.inProgress) {
      return <AppLoadingRoot />
    }

    if (isEmpty(authMethods) || !canSso) {
      return <Navigate to={{ pathname: '/login/basic', search: location.search }} />
    }

    return (
      <LandingPage scrollPage={true} loading={loginRequest.inProgress} css={getLoginFormSsoStyle}>
        {this.renderContent()}
      </LandingPage>
    )
  }

  renderContent() {
    const {
      authMethods,
      router: { location },
    } = this.props
    const ssoMethods = authMethods?.sso_methods ?? []

    return (
      <EuiFlexGroup alignItems='center' direction='column'>
        <EuiFlexItem>
          <EuiFlexGroup>
            {ssoMethods.map((ssoMethod) => {
              const { sso_type, name, url } = ssoMethod
              const loginUrl = getLoginUrl({ ssoUrl: url, location })

              return (
                <EuiFlexItem key={`${sso_type}:${url}`}>
                  <EuiButton
                    fill={true}
                    className='login-oauth'
                    href={loginUrl}
                    data-test-id='start-sso-button'
                  >
                    <FormattedMessage
                      id='log-in-with-sso-provider'
                      defaultMessage='Log in with {provider}'
                      values={{ provider: name }}
                    />
                  </EuiButton>
                </EuiFlexItem>
              )
            })}
          </EuiFlexGroup>
        </EuiFlexItem>

        <EuiFlexItem>
          <CuiRouterLinkButtonEmpty
            data-test-id='switch-to-password'
            to={{ pathname: '/login/basic', search: location.search }}
          >
            <EuiText size='s'>
              <FormattedMessage
                id='log-in.switch-to-password'
                defaultMessage='Switch to password login'
              />
            </EuiText>
          </CuiRouterLinkButtonEmpty>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }
}

export default SsoBasedLogin
